import { RedirectRequest } from '@azure/msal-browser';
import { environment } from '@environment';

const DEFAULT_LOGIN_SCOPES = ['openid', 'offline_access'];

interface ResolvedMsalConfig {
  clientId: string;
  authority: string;
  knownAuthorities: string[];
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function normalizePath(path: string | undefined, fallback: string): string {
  const value = (path ?? fallback).trim();
  if (!value) return fallback;
  return value.startsWith('/') ? value : `/${value}`;
}

function readMsalConfig(): ResolvedMsalConfig {
  return {
    clientId: environment.msal?.clientId?.trim() ?? '',
    authority: environment.msal?.authority?.trim() ?? '',
    knownAuthorities: unique((environment.msal?.knownAuthorities ?? []).map(v => v.trim()))
  };
}

function requireMsalConfig(): ResolvedMsalConfig {
  const config = readMsalConfig();

  if (!config.clientId || !config.authority || config.knownAuthorities.length === 0) {
    throw new Error(
      'MSAL/B2C no está configurado. Define clientId, authority y knownAuthorities en el environment activo.'
    );
  }

  return config;
}

export function buildAbsoluteUrl(path: string | undefined, fallback = '/login'): string {
  const normalized = normalizePath(path, fallback);
  return new URL(normalized, window.location.origin).toString();
}

export function isMsalConfigured(): boolean {
  const config = readMsalConfig();
  return Boolean(config.clientId && config.authority && config.knownAuthorities.length > 0);
}

export function buildAuthScopes(): string[] {
  const scopes = unique([
    ...(environment.msal?.loginScopes ?? DEFAULT_LOGIN_SCOPES),
    ...(environment.msal?.apiScopes ?? [])
  ]);

  return scopes.length > 0 ? scopes : DEFAULT_LOGIN_SCOPES;
}

export function buildLoginRequest(): RedirectRequest {
  const config = requireMsalConfig();

  const request: RedirectRequest = {
    authority: config.authority,
    scopes: buildAuthScopes()
  };

  return request;
}

export function resolveMsalAuthority(): string {
  return requireMsalConfig().authority;
}

export function resolveMsalClientId(): string {
  return requireMsalConfig().clientId;
}

export function resolveKnownAuthorities(): string[] {
  return requireMsalConfig().knownAuthorities;
}

export function uniqueScopes(scopes: string[]): string[] {
  return unique(scopes);
}
